import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Constellation, { ConstellationData } from './Constellation';
import { CONSTELLATION_DATA } from './ConstellationData';
import TutorialProgressService, { RAILROAD_SEQUENCE } from './TutorialProgressService';
import WispyStars from './WispyStars';
import BackgroundStars from './BackgroundStars';
import BeaconIndicator from './BeaconIndicator';
import { useLanguage } from '../Providers/LanguageContext';
import { useAuthContext } from '../../hooks/useAuthContext';
import styles from './StarMap.module.css';
import bgStyles from './BackgroundStars.module.css';

const RAILROAD_ANIM_MS = 700;
const RAILROAD_ANIM_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

// Compute a tight wrap so every constellation occupies the same rendered size
// in guided mode, regardless of its base position.scale. Targets ~75% of the
// shorter viewport dimension (capped at 480px so it doesn't get ridiculous on
// ultrawide displays).
function computeRailroadZoom(scale: number, rect: DOMRect, zoomFactor: number = 1): number {
  const targetSize = Math.min(rect.width * 0.8, rect.height * 0.68, 600) * zoomFactor;
  const baseSize = 100 * scale;
  return targetSize / baseSize;
}

// How much to bias the aim toward the star-average vs the geometric image
// center. The star centroid is a noisy proxy for the illustration's visual
// centroid — outlier stars (like Bear's headlight at x=96, y=37) pull the
// average away from where the eye reads the constellation. Half-weighting
// splits the difference: still acknowledges asymmetry in the layout, but
// doesn't over-commit to it.
const STAR_CENTROID_WEIGHT = 0.45;

// Aim point in local image coords (0-100), after the constellation's
// rotation is applied around the image center. Stars are authored relative
// to the unrotated 100x100 image; the parent div rotates the whole thing
// around (50,50), so we rotate the star centroid to match what the user
// actually sees, then blend toward image center per STAR_CENTROID_WEIGHT.
function computeVisualCentroid(stars: { x: number; y: number }[], rotationDeg: number) {
  if (stars.length === 0) return { x: 50, y: 50 };
  const avgX = stars.reduce((s, p) => s + p.x, 0) / stars.length;
  const avgY = stars.reduce((s, p) => s + p.y, 0) / stars.length;
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = (avgX - 50) * STAR_CENTROID_WEIGHT;
  const dy = (avgY - 50) * STAR_CENTROID_WEIGHT;
  return {
    x: 50 + dx * cos - dy * sin,
    y: 50 + dx * sin + dy * cos,
  };
}

interface StarMapProps {
  onConstellationClick?: (constellation: ConstellationData) => void;
}

function StarMap({ onConstellationClick }: StarMapProps) {
  const [constellations] = useState<ConstellationData[]>(CONSTELLATION_DATA);
  const [, setRefresh] = useState(0); // Force re-render when progress changes
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Dev-only progress reset. Gated identically to the AIPlanPanel check in
  // TutorialGame.tsx so the same maintainer account sees both. Lets the
  // maintainer test the tutorial railroad from a fresh-account state without
  // having to clear localStorage by hand.
  const { userProfile, user } = useAuthContext();
  const showDevReset =
    process.env.NODE_ENV === 'development' ||
    (userProfile?.username?.toLowerCase().startsWith('dscvr') ?? false) ||
    user?.email === 'dion@flowacademy.co.nz';

  const handleDevResetProgress = useCallback(() => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Reset ALL tutorial progress?\n\nThis clears constellation unlocks, completed challenges, and tutorial-intro popups for this browser. You will need to start from Bear tutorial 1.'
    );
    if (!confirmed) return;
    TutorialProgressService.resetProgress();
    setRefresh(prev => prev + 1);
  }, []);

  // When returning from a tutorial, focus the constellation the player was on
  // instead of defaulting to Bear. Read once on mount so subsequent navigation
  // (e.g. browser back) doesn't keep re-focusing.
  const initialFocusIndexRef = useRef<number | null>(null);
  if (initialFocusIndexRef.current === null) {
    const focusName = (location.state as { focusConstellation?: string } | null)?.focusConstellation;
    const idx = focusName
      ? RAILROAD_SEQUENCE.indexOf(focusName as typeof RAILROAD_SEQUENCE[number])
      : -1;
    initialFocusIndexRef.current = idx >= 0 ? idx : 0;
  }
  const initialFocusIndex = initialFocusIndexRef.current;

  // Pan and zoom state
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1); // Start at 1.0 (which displays as 100%)
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Touch state for mobile
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);

  // Railroad navigation state
  const [isLocked, setIsLocked] = useState<boolean>(() => {
    const saved = localStorage.getItem('starMapLocked');
    return saved === null ? true : saved === 'true';
  });
  const [railroadIndex, setRailroadIndex] = useState<number>(initialFocusIndex);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const animTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Frame a named constellation centered in the viewport. If no zoom is
  // provided we compute one that fits the constellation tightly on screen
  // (used by railroad transitions). Free-mode callers can pass an explicit
  // zoom (e.g. handleResetView) to preserve old behaviour.
  const computeFocusFor = useCallback(
    (name: string, rect: DOMRect, targetZoom?: number) => {
      const c = CONSTELLATION_DATA.find((c) => c.name === name);
      if (!c) return null;
      const zoom = targetZoom ?? computeRailroadZoom(c.position.scale, rect, c.railroadZoomFactor);
      const canvasW = rect.width * 3;
      const canvasH = rect.height * 3;
      // The constellation div is 100px wide; its CSS transform scales/rotates
      // around the div's center (transform-origin defaults to 50% 50%), so the
      // visual image center stays at (left + 50px, top + 50px) regardless of
      // scale. Then we shift by the rotated star centroid (scaled) to aim at
      // the actual bear/shape, not the empty corner of the image.
      const centroid = computeVisualCentroid(c.stars, c.position.rotation);
      const aimX =
        (canvasW * c.position.x) / 100 + 50 + (centroid.x - 50) * c.position.scale;
      const aimY =
        (canvasH * c.position.y) / 100 + 50 + (centroid.y - 50) * c.position.scale;
      return {
        x: rect.width / 2 - aimX * zoom,
        y: rect.height / 2 - aimY * zoom,
        zoom,
      };
    },
    []
  );

  // Initialize: locked mode always frames Bear; free mode restores last view.
  useEffect(() => {
    if (!containerRef.current || isInitialized) return;
    const rect = containerRef.current.getBoundingClientRect();

    if (isLocked) {
      const focusName = RAILROAD_SEQUENCE[initialFocusIndex] ?? 'Bear';
      const focus = computeFocusFor(focusName, rect);
      if (focus) {
        setPanPosition({ x: focus.x, y: focus.y });
        setZoomLevel(focus.zoom);
        setRailroadIndex(initialFocusIndex);
      }
    } else {
      const savedPosition = localStorage.getItem('starMapPosition');
      const savedZoom = localStorage.getItem('starMapZoom');
      if (savedPosition && savedZoom) {
        setPanPosition(JSON.parse(savedPosition));
        setZoomLevel(parseFloat(savedZoom));
      } else {
        // Free mode first-visit fallback: 2.0x relaxed Bear framing.
        const focus = computeFocusFor('Bear', rect, 2.0);
        if (focus) {
          setPanPosition({ x: focus.x, y: focus.y });
          setZoomLevel(focus.zoom);
        }
      }
    }

    setIsInitialized(true);
  }, [isInitialized, isLocked, computeFocusFor]);

  // Persist free-mode pan/zoom only. Locked mode always boots at Bear, so
  // raw pan/zoom doesn't need persisting.
  useEffect(() => {
    if (!isInitialized || isLocked) return;
    localStorage.setItem('starMapPosition', JSON.stringify(panPosition));
    localStorage.setItem('starMapZoom', zoomLevel.toString());
  }, [panPosition, zoomLevel, isInitialized, isLocked]);

  // Persist lock toggle.
  useEffect(() => {
    localStorage.setItem('starMapLocked', isLocked ? 'true' : 'false');
  }, [isLocked]);

  // Clean up the trailing animation timer on unmount so we don't fire setState
  // after the component is gone.
  useEffect(() => {
    return () => {
      if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
    };
  }, []);

  // Drives a smooth cinematic zoom/pan to a constellation by name.
  const animateTo = useCallback(
    (name: string, newIndex: number) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const focus = computeFocusFor(name, rect);
      if (!focus) return;
      if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
      setIsAnimating(true);
      setPanPosition({ x: focus.x, y: focus.y });
      setZoomLevel(focus.zoom);
      setRailroadIndex(newIndex);
      animTimeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
        animTimeoutRef.current = null;
      }, RAILROAD_ANIM_MS + 50);
    },
    [computeFocusFor]
  );

  // Right arrow is enabled when either the next constellation is already
  // unlocked, or the current one passes the unlock predicate this session.
  const canAdvance = useMemo(() => {
    if (railroadIndex >= RAILROAD_SEQUENCE.length - 1) return false;
    const currentName = RAILROAD_SEQUENCE[railroadIndex];
    const nextName = RAILROAD_SEQUENCE[railroadIndex + 1];
    return (
      TutorialProgressService.isConstellationUnlocked(nextName) ||
      TutorialProgressService.canAdvanceFrom(currentName)
    );
  }, [railroadIndex]);

  const goPrev = useCallback(() => {
    if (railroadIndex <= 0) return;
    animateTo(RAILROAD_SEQUENCE[railroadIndex - 1], railroadIndex - 1);
  }, [railroadIndex, animateTo]);

  const goNext = useCallback(() => {
    if (!canAdvance) return;
    const nextName = RAILROAD_SEQUENCE[railroadIndex + 1];
    TutorialProgressService.markUnlocked(nextName);
    animateTo(nextName, railroadIndex + 1);
  }, [canAdvance, railroadIndex, animateTo]);

  const toggleLock = useCallback(() => {
    setIsLocked((prev) => {
      const nextLocked = !prev;
      if (!containerRef.current) return nextLocked;
      const rect = containerRef.current.getBoundingClientRect();

      if (nextLocked) {
        // Snap to the current railroad constellation when re-entering locked mode.
        const focus = computeFocusFor(RAILROAD_SEQUENCE[railroadIndex], rect);
        if (focus) {
          if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
          setIsAnimating(true);
          setPanPosition({ x: focus.x, y: focus.y });
          setZoomLevel(focus.zoom);
          animTimeoutRef.current = setTimeout(() => {
            setIsAnimating(false);
            animTimeoutRef.current = null;
          }, RAILROAD_ANIM_MS + 50);
        }
      } else {
        // Entering free mode: restore the player's last free-surf view if we
        // have one saved. Otherwise leave the railroad framing as a natural
        // starting point.
        const savedPosition = localStorage.getItem('starMapPosition');
        const savedZoom = localStorage.getItem('starMapZoom');
        if (savedPosition && savedZoom) {
          if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
          setIsAnimating(true);
          setPanPosition(JSON.parse(savedPosition));
          setZoomLevel(parseFloat(savedZoom));
          animTimeoutRef.current = setTimeout(() => {
            setIsAnimating(false);
            animTimeoutRef.current = null;
          }, RAILROAD_ANIM_MS + 50);
        }
      }

      return nextLocked;
    });
  }, [railroadIndex, computeFocusFor]);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Constrain pan position to keep content visible
  const constrainPan = (x: number, y: number, zoom: number) => {
    if (!containerRef.current) return { x, y };

    const rect = containerRef.current.getBoundingClientRect();
    const canvasWidth = rect.width * 3;
    const canvasHeight = rect.height * 3;

    // Calculate the scaled canvas dimensions
    const scaledCanvasWidth = canvasWidth * zoom;
    const scaledCanvasHeight = canvasHeight * zoom;

    // Allow some padding so you can see a bit beyond the edges
    const padding = 100;

    // Max pan values (how far right/down you can pan)
    const maxX = padding;
    const maxY = padding;

    // Min pan values (how far left/up you can pan)
    // When fully panned left/up, the right/bottom edge of canvas should be at viewport edge
    const minX = rect.width - scaledCanvasWidth - padding;
    const minY = rect.height - scaledCanvasHeight - padding;

    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y))
    };
  };

  // Listen for progress changes
  useEffect(() => {
    const handleStorageChange = () => {
      setRefresh(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Mouse drag handlers (desktop only)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMobile || isLocked) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || isMobile) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // Constrain pan to keep content visible
    const constrainedPosition = constrainPan(newX, newY, zoomLevel);
    setPanPosition(constrainedPosition);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile || isLocked) return;

    if (e.touches.length === 1) {
      // Single touch - start panning
      const touch = e.touches[0];
      setTouchStart({ x: touch.clientX - panPosition.x, y: touch.clientY - panPosition.y });
      setIsDragging(true);
    } else if (e.touches.length === 2) {
      // Two finger touch - prepare for pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setLastTouchDistance(distance);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || isLocked) return;

    if (e.touches.length === 1 && touchStart && isDragging) {
      // Single touch - pan
      e.preventDefault(); // Prevent scrolling
      const touch = e.touches[0];
      const newX = touch.clientX - touchStart.x;
      const newY = touch.clientY - touchStart.y;

      const constrainedPosition = constrainPan(newX, newY, zoomLevel);
      setPanPosition(constrainedPosition);
    } else if (e.touches.length === 2 && lastTouchDistance !== null) {
      // Two finger touch - pinch zoom
      e.preventDefault(); // Prevent default pinch behavior

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const newDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      // Calculate zoom change
      const scale = newDistance / lastTouchDistance;
      const newZoom = Math.min(Math.max(1, zoomLevel * scale), 2); // Range: 1.0 to 2.0

      // Calculate center point between two fingers
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
        const centerY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

        // Point in the canvas that's currently under the pinch center (before zoom)
        const canvasPointX = (centerX - panPosition.x) / zoomLevel;
        const canvasPointY = (centerY - panPosition.y) / zoomLevel;

        // After zoom, adjust pan so that same point is under pinch center
        const newPanX = centerX - canvasPointX * newZoom;
        const newPanY = centerY - canvasPointY * newZoom;

        // Constrain the pan to keep content visible
        const constrainedPosition = constrainPan(newPanX, newPanY, newZoom);
        setPanPosition(constrainedPosition);
      }

      setZoomLevel(newZoom);
      setLastTouchDistance(newDistance);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile) return;

    if (e.touches.length === 0) {
      // All touches ended
      setTouchStart(null);
      setIsDragging(false);
      setLastTouchDistance(null);
    } else if (e.touches.length === 1) {
      // Went from 2 touches to 1 - reset to single touch pan
      setLastTouchDistance(null);
      const touch = e.touches[0];
      setTouchStart({ x: touch.clientX - panPosition.x, y: touch.clientY - panPosition.y });
    }
  };

  // Zoom handler - zoom towards cursor position
  const handleWheel = (e: React.WheelEvent) => {
    if (isMobile || isLocked) return;
    e.preventDefault();

    const zoomSpeed = 0.2;
    const delta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
    const newZoom = Math.min(Math.max(1, zoomLevel + delta), 2); // Range: 1.0 (100%) to 2.0 (200%)

    // Calculate zoom towards cursor position
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      // Mouse position in viewport
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Point in the canvas that's currently under the mouse (before zoom)
      const canvasPointX = (mouseX - panPosition.x) / zoomLevel;
      const canvasPointY = (mouseY - panPosition.y) / zoomLevel;

      // After zoom, adjust pan so that same point is under mouse
      const newPanX = mouseX - canvasPointX * newZoom;
      const newPanY = mouseY - canvasPointY * newZoom;

      // Constrain the pan to keep content visible
      const constrainedPosition = constrainPan(newPanX, newPanY, newZoom);
      setPanPosition(constrainedPosition);
    }

    setZoomLevel(newZoom);
  };

  // Zoom controls - zoom toward viewport center
  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel + 0.2, 2); // Max 2.0 (displays as 200%)

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      // Zoom toward the center of the viewport
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Point in the canvas that's currently at viewport center (before zoom)
      const canvasPointX = (centerX - panPosition.x) / zoomLevel;
      const canvasPointY = (centerY - panPosition.y) / zoomLevel;

      // After zoom, adjust pan so that same point stays at viewport center
      const newPanX = centerX - canvasPointX * newZoom;
      const newPanY = centerY - canvasPointY * newZoom;

      // Constrain the pan to keep content visible
      const constrainedPosition = constrainPan(newPanX, newPanY, newZoom);
      setPanPosition(constrainedPosition);
    }

    setZoomLevel(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - 0.2, 1); // Min 1.0 (displays as 100%)

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      // Zoom toward the center of the viewport
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Point in the canvas that's currently at viewport center (before zoom)
      const canvasPointX = (centerX - panPosition.x) / zoomLevel;
      const canvasPointY = (centerY - panPosition.y) / zoomLevel;

      // After zoom, adjust pan so that same point stays at viewport center
      const newPanX = centerX - canvasPointX * newZoom;
      const newPanY = centerY - canvasPointY * newZoom;

      // Constrain the pan to keep content visible
      const constrainedPosition = constrainPan(newPanX, newPanY, newZoom);
      setPanPosition(constrainedPosition);
    }

    setZoomLevel(newZoom);
  };

  const handleResetView = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // Use the original 2.0x free-mode framing here, not the tight railroad
    // zoom — this button only shows up in free-surf mode.
    const focus = computeFocusFor('Bear', rect, 2.0);
    if (focus) {
      setPanPosition({ x: focus.x, y: focus.y });
      setZoomLevel(focus.zoom);
    }
  };

  const nextTutorial = TutorialProgressService.getNextIncompleteTutorial();

  // Calculate beacon's world position (position in viewport coordinates)
  const beaconWorldPosition = useMemo(() => {
    if (!nextTutorial || !containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const canvasWidth = rect.width * 3;
    const canvasHeight = rect.height * 3;

    // Find the constellation with the next tutorial
    const targetConstellation = constellations.find(c => c.name === nextTutorial.constellation);
    if (!targetConstellation) return null;

    const targetStar = targetConstellation.stars[nextTutorial.starIndex];
    if (!targetStar) return null;

    // Calculate constellation's top-left position in canvas coordinates
    const constellationX = (canvasWidth * targetConstellation.position.x) / 100;
    const constellationY = (canvasHeight * targetConstellation.position.y) / 100;

    // Constellation image is 100px base size
    const constellationSize = 100 * targetConstellation.position.scale;

    // Star position is relative to constellation image (in percentage)
    // Stars are positioned relative to the image's top-left corner
    const starOffsetX = (targetStar.x / 100) * constellationSize;
    const starOffsetY = (targetStar.y / 100) * constellationSize;

    // Absolute position in canvas (constellation pos + star offset relative to constellation)
    const starCanvasX = constellationX + starOffsetX;
    const starCanvasY = constellationY + starOffsetY;

    // Transform to viewport coordinates
    const starViewportX = starCanvasX * zoomLevel + panPosition.x;
    const starViewportY = starCanvasY * zoomLevel + panPosition.y;

    return {
      x: starViewportX,
      y: starViewportY
    };
  }, [nextTutorial, constellations, panPosition, zoomLevel, containerRef]);

  const handleConstellationClick = (constellation: ConstellationData) => {
    const progress = TutorialProgressService.getConstellationProgress(constellation.name);
    onConstellationClick?.(constellation);
  };

  const handleStarClick = (constellationName: string, starIndex: number) => {
    // Navigate to the tutorial game page
    navigate(`/tutorials/${constellationName}/${starIndex + 1}`);
  };

  // Single transition expression shared by all parallax layers + canvas.
  // Three states: live drag (none), railroad cinematic, ordinary settle.
  const layerTransition = isDragging
    ? 'none'
    : isAnimating
      ? `transform ${RAILROAD_ANIM_MS}ms ${RAILROAD_ANIM_EASING}`
      : 'transform 0.2s ease-out';

  const currentRailName = RAILROAD_SEQUENCE[railroadIndex];
  const nextRailName = RAILROAD_SEQUENCE[railroadIndex + 1] ?? null;
  const atRailEnd = railroadIndex >= RAILROAD_SEQUENCE.length - 1;
  const atRailStart = railroadIndex <= 0;
  const nextLockTitle = !canAdvance && nextRailName
    ? t('tutorials.lockedNextHint', { constellation: currentRailName })
    : t('tutorials.nextConstellation');

  return (
    <div
      className={styles.starMap}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        cursor: isLocked ? 'default' : (isDragging ? 'grabbing' : (isMobile ? 'default' : 'grab')),
        touchAction: 'none' // Prevent default touch behaviors
      }}
    >
      {/* Dev-only progress reset. Top-left so it doesn't collide with the
          lock toggle (bottom-right) or zoom controls (bottom-right above lock).
          Always visible to the maintainer regardless of lock state. */}
      {showDevReset && (
        <button
          type="button"
          onClick={handleDevResetProgress}
          className={styles.devResetButton}
          title="Dev: reset all tutorial progress"
          aria-label="Dev: reset all tutorial progress"
        >
          <span className={styles.devResetIcon} aria-hidden="true">↺</span>
          <span className={styles.devResetLabel}>Reset progress</span>
        </button>
      )}

      {/* Lock toggle — bottom-right pill. Shows the current state on top and
          the action that clicking will take underneath, so first-time players
          understand what flipping it does. */}
      <button
        type="button"
        onClick={toggleLock}
        className={styles.lockToggle}
        aria-pressed={isLocked}
      >
        <span className={styles.lockToggleMain}>
          <span className={styles.lockToggleIcon} aria-hidden="true">{isLocked ? '🔒' : '🔓'}</span>
          <span className={styles.lockToggleLabel}>
            {isLocked ? t('tutorials.lockedStateLabel') : t('tutorials.freeStateLabel')}
          </span>
        </span>
        <span className={styles.lockToggleHint}>
          {isLocked ? t('tutorials.clickToFreeRoam') : t('tutorials.clickForStarPath')}
        </span>
      </button>

      {/* Railroad navigation arrows — chevrons matching the MainPage
          piece-carousel style. Positioned to flank the currently-framed
          constellation. Hidden entirely at the ends of the sequence (no
          previous on Bear, no next on Virus); disabled-locked styling only
          applies mid-sequence when the user can't yet advance. */}
      {!atRailStart && (
        <button
          type="button"
          onClick={goPrev}
          className={`${styles.railroadArrow} ${styles.railroadArrowLeft}`}
          title={t('tutorials.previousConstellation')}
          aria-label={t('tutorials.previousConstellation')}
        >
          <svg viewBox="0 0 24 40" aria-hidden="true" focusable="false">
            <path d="M18 4 L6 20 L18 36" />
          </svg>
        </button>
      )}
      {!atRailEnd && (
        <button
          type="button"
          onClick={goNext}
          className={`${styles.railroadArrow} ${styles.railroadArrowRight} ${!canAdvance ? styles.railroadArrowDisabled : ''}`}
          title={nextLockTitle}
          aria-label={nextLockTitle}
          disabled={!canAdvance}
        >
          <svg viewBox="0 0 24 40" aria-hidden="true" focusable="false">
            <path d="M6 4 L18 20 L6 36" />
          </svg>
          {!canAdvance && (
            <span className={styles.railroadArrowLockBadge} aria-hidden="true">🔒</span>
          )}
        </button>
      )}

      {/* Zoom controls — hidden in locked mode (interactions are gated). */}
      {!isLocked && (
        <div className={styles.zoomControls}>
          <button onClick={handleZoomIn} className={styles.zoomButton} title="Zoom In">+</button>
          <button onClick={handleZoomOut} className={styles.zoomButton} title="Zoom Out">−</button>
          <button onClick={handleResetView} className={styles.zoomButton} title="Reset View">⌂</button>
          <div className={styles.zoomLevel}>{Math.round(zoomLevel * 50)}%</div>
        </div>
      )}

      {/* Hint text — only relevant in free-surf mode. */}
      {!isLocked && (
        <div className={styles.dragHint}>
          {isMobile ? t('tutorials.dragToExploreMobile') : t('tutorials.dragToExploreDesktop')}
        </div>
      )}

      {/* Beacon indicator - shows direction to next tutorial when off-screen */}
      {beaconWorldPosition && (
        <BeaconIndicator
          beaconWorldX={beaconWorldPosition.x}
          beaconWorldY={beaconWorldPosition.y}
          containerRef={containerRef}
        />
      )}

      {/* Wispy stars - not part of parallax, just ambient animation */}
      <WispyStars />

      {/* Parallax layers - background stars at different depths */}

      {/* Pin-light layer 3 - 94% speed (furthest) */}
      <div
        className={styles.parallaxLayer}
        style={{
          transform: `translate(${panPosition.x * 0.94}px, ${panPosition.y * 0.94}px) scale(${1 + (zoomLevel - 1) * 0.94})`,
          transformOrigin: '0 0',
          transition: layerTransition
        }}
      >
        <div className={bgStyles.backgroundStars}>
          <div className={bgStyles.pinLightLayer3}></div>
        </div>
      </div>

      {/* Pin-light layer 2 - 96% speed */}
      <div
        className={styles.parallaxLayer}
        style={{
          transform: `translate(${panPosition.x * 0.96}px, ${panPosition.y * 0.96}px) scale(${1 + (zoomLevel - 1) * 0.96})`,
          transformOrigin: '0 0',
          transition: layerTransition
        }}
      >
        <div className={bgStyles.backgroundStars}>
          <div className={bgStyles.pinLightLayer2}></div>
        </div>
      </div>

      {/* Pin-light layer 1 & Star layer 2 - 98% speed */}
      <div
        className={styles.parallaxLayer}
        style={{
          transform: `translate(${panPosition.x * 0.98}px, ${panPosition.y * 0.98}px) scale(${1 + (zoomLevel - 1) * 0.98})`,
          transformOrigin: '0 0',
          transition: layerTransition
        }}
      >
        <div className={bgStyles.backgroundStars}>
          <div className={bgStyles.pinLightLayer1}></div>
          <div className={bgStyles.starLayer2}></div>
        </div>
      </div>

      {/* Star layer 1 - 100% speed (closest background layer) */}
      <div
        className={styles.parallaxLayer}
        style={{
          transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
          transformOrigin: '0 0',
          transition: layerTransition
        }}
      >
        <div className={bgStyles.backgroundStars}>
          <div className={bgStyles.starLayer1}></div>
        </div>
      </div>

      {/* Constellation layer - 100% speed (foreground) */}
      <div
        className={styles.virtualCanvas}
        style={{
          transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
          transformOrigin: '0 0',
          transition: layerTransition
        }}
      >
        {constellations.map((constellation) => (
          <Constellation
            key={constellation.name}
            constellation={constellation}
            onClick={() => handleConstellationClick(constellation)}
            onStarClick={handleStarClick}
            nextTutorial={nextTutorial}
          />
        ))}
      </div>
    </div>
  );
}

export default StarMap; 